import { useCallback, useEffect, useRef, useState } from 'react';
import { isMacOs } from 'react-device-detect';

import { useCommandRegistry } from 'lib/commands/context';
import { useActiveModalsContext } from 'lib/contexts/ActiveModalsContext';

// Time window in ms for multi-key sequences
const SEQUENCE_TIMEOUT_MS = 1000;

/**
 * Keyboard shortcut types
 */
export enum ShortcutType {
  COMMAND = 'command', // Command shortcuts like ⌘K
  SEQUENCE = 'sequence', // Sequence shortcuts like G then B
}

type UseKeyboardShortcutsSharedProps = {
  isEnabled: boolean;
};

/**
 * Hook to handle sequence keyboard shortcuts like "G+B"
 */
export const useSequenceShortcuts = ({ isEnabled }: UseKeyboardShortcutsSharedProps) => {
  const [sequence, setSequence] = useState<string[]>([]);
  const sequenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { executeSequence } = useCommandRegistry();
  const { isAnyModalOpen } = useActiveModalsContext();

  // Reset the sequence after a timeout
  const resetSequence = useCallback(() => {
    setSequence([]);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isEnabled || isAnyModalOpen) {
        return;
      }

      // Don't capture shortcuts when user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return;
      }

      // Ignore if any modifier keys are pressed
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      const key = e.key.toLowerCase();

      // Only track alphabetical keys for sequences
      if (/^[a-z]$/.test(key)) {
        e.preventDefault();

        // Set a timeout to reset the sequence
        if (sequenceTimeoutRef.current) {
          clearTimeout(sequenceTimeoutRef.current);
        }

        sequenceTimeoutRef.current = setTimeout(resetSequence, SEQUENCE_TIMEOUT_MS);

        // Add the key to the sequence
        setSequence((prev) => {
          const newSequence = [...prev, key];

          // Use the registry to check and execute the sequence
          const executed = executeSequence(newSequence);

          if (executed) {
            // Reset the sequence if command was executed
            // Using setTimeout here to ensure the sequence is reset after the command is executed
            setTimeout(resetSequence, 0);
            return [];
          }

          return newSequence;
        });
      }
    },
    [isEnabled, executeSequence, resetSequence, isAnyModalOpen],
  );

  // Listen for key presses to detect sequences
  useEffect(() => {
    // Only capture shortcuts if enabled and no modals are open
    if (isEnabled && !isAnyModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      if (sequenceTimeoutRef.current) {
        clearTimeout(sequenceTimeoutRef.current);
      }
    };
  }, [resetSequence, isEnabled, handleKeyDown, isAnyModalOpen]);

  return sequence;
};

/**
 * Main hook to set up global keyboard shortcuts.
 *
 * Feature-specific keyboard shortcuts (like "mod+k" for command palette)
 * are handled in their respective feature hooks.
 */
export const useKeyboardShortcuts = ({ isEnabled }: UseKeyboardShortcutsSharedProps) => {
  // Set up the sequence shortcuts
  const currentSequence = useSequenceShortcuts({ isEnabled });

  return {
    currentSequence,
  };
};

/**
 * Utility to format a shortcut for display.
 */
export const formatShortcut = (keys: string[]): string[] =>
  keys.map((fragment) => {
    const lower = fragment.toLowerCase();

    if (['mod', 'command', 'ctrl'].includes(lower)) {
      return isMacOs ? '⌘' : 'Ctrl';
    } else if (lower === 'alt') {
      return isMacOs ? '⌥' : 'Alt';
    } else if (lower.startsWith('key')) {
      return lower.slice(3);
    } else if (lower === 'arrowup') {
      return '↑';
    } else if (lower === 'arrowdown') {
      return '↓';
    } else if (lower === 'arrowleft') {
      return '←';
    } else if (lower === 'arrowright') {
      return '→';
    }

    return fragment;
  });
